import {MemoItemProperties, ObjectProperties} from './PropertyEditor';
import {ProgressBar, SearchableMenu, ShowDropdown} from './DomMenu';
import { ImageTools } from './ImageTools';
import { ScriptHost } from './ScriptHost';
import { EL, effectAllowed, hide, renderIcon2, show } from './util/DomUtils';
import {
    compareObjects,
    filterObject,
    CancelledError,
    shallowClone,
    deepEqual,
    debounced,
    BoundMethod, attrListToKv
} from './util/CoreUtils';
import { PropertyObject, Point, PointType } from './lib/memoTypes';
import $ from 'jquery';
import 'jquery-ui/ui/widgets/resizable';
import { IDocument, IDocumentCommit} from './IMemoRemote';
import {CallableDisposableStack, observable, signal, StackDisposable} from './lib/signal';
import { DomWindow, DomWindowConfig} from './DomWindow';
import { Command, DynamicToolbarSource} from './ActionManager';
import { SelectionManager} from './memo/MemoSelectionManager';
import { Markdown } from './util/Markdown';
import { getTitleOfURL } from "./util/AppUtils";
import { UiContext } from "./util/UiContext";
import {IEditorWindow} from "./IEditorWindow";
import {slot} from "./lib/slot";
import {InMemoryDocument} from "./db/WritableDocument";
import {Minimap} from "./memo/Minimap";
import {IHierarchy} from "./lib/hierarchy";
import {Activity} from "./ui/Activity";

const MIME_TYPE_ITEMS = 'application/vnd.pinboard-items+json';
const LONG_PRESS_TIMEOUT = 750;
var isTouchOnlyDevice = false;
try {
    if (window.matchMedia("(any-hover: none)").matches) {
        isTouchOnlyDevice=true;
    }
} catch {}


type RubberbandRect = {
    x: number, y: number, width: number, height: number;
}
export type MemoOptions = {
    interactive?: boolean,
    itemConstructor?: typeof MemoItem,
    selectedOutlineStyle?: string,
    alwaysAllowIconClick?: boolean,
    newrectTb?: string,
    showMinimap?: boolean,
    allowSelect?: boolean,
}

@StackDisposable.Mixin
export class Memo implements StackDisposable, DynamicToolbarSource, IHierarchy {
    declare [Symbol.dispose]: CallableDisposableStack;
    declare dtor: DisposableStack;

    items:{ [key:string] : MemoItem };
    get documentId() {
        return this.document?.documentId;
    }
    undoStack: PropertyObject[] = [];
    redoStack: PropertyObject[] = [];
    @observable
    public accessor document : IDocument | null;
    readonly beforeUnloadDocument = signal<() => void | Promise<void>>();

    private rubberband: HTMLDivElement = null;
    rubberbandRect: RubberbandRect = null;
    private rubberbandStartPos: PointType = null;
    public readonly scrollContainer: HTMLElement;
    readonly container: HTMLDivElement;
    private readonly loadIndicator: HTMLDivElement;
    private readonly minimap: Minimap;
    isInitialRender: boolean;
    memoWidth = 2800;
    memoHeight = 2800;
    itemDefaultProps: PropertyObject = {
        "backgroundColor":"rgba(200,200,255,0.4)",
        "border": "1px solid #448",
        "text": "",
        "width": 228,
        "height":32,
        "textAlign": "left",
    };
    get validItems(): MemoItem[] {
        return this.items ? Object.values(this.items).filter(x => !x.isDeleted) : [];
    }
    
    public readonly options: MemoOptions;
    public get interactive() : boolean {
        return this.options.interactive;
    }
    
    private _readonly = true;
    private showMinimap;

    constructor(
        scrollContainer: HTMLElement | null,
        public readonly sel: SelectionManager<Memo, MemoItem>,
        document: IDocument | null,
        options: MemoOptions
    ) {
        if (document && !('updated' in document && 'items' in document))
            throw new Error("new Memo(): Invalid argument for document");
        this.items = {};
        this.options = Object.assign({
            interactive: true,
            readonly: false,
            itemConstructor: MemoItem,
            selectedOutlineStyle: "3px dashed #00f",
            newrectTb: 'newrect',
            showMinimap: true,
            allowSelect: true,
        }, options);
        this.scrollContainer = scrollContainer || EL('div', {});
        this.scrollContainer.innerHTML = '';
        this.scrollContainer.classList.add( 'memoScrollContainer');
        this.container = EL('div',{class:'memoArea',appendTo:this.scrollContainer});
        this.loadIndicator = EL('div', {class:'loadindicator center',style:'display:none',appendTo:this.scrollContainer}, EL('div', {}), EL('div', {}), EL('div', {}));
        if (this.options.showMinimap) {
            const minimapPos = (localStorage.minimapPosition || 'right bottom').split(' ');
            Minimap;  // ensure memo-minimap customElement is loaded
            this.minimap = EL('memo-minimap', {
                appendTo: this.scrollContainer,
                class: 'minimap',
                style: {
                    position: 'absolute',
                    [minimapPos[0]]: '5px',
                    [minimapPos[1]]: '5px',
                    maxWidth: '180px',
                    maxHeight: '180px',
                    zIndex: '1000000'
                }
            },) as Minimap;
            this.minimap.memo = this;
            const hideMinimap = debounced(parseInt(localStorage.minimapTimeout) || 1500, () => {
                this.minimap.classList.add('hidden');
            });
            this.showMinimap = debounced(50, () => {
                const minimapSize = Math.min(parseInt(localStorage.minimapMaxSize) || 180, Math.min(this.scrollContainer.clientWidth, this.scrollContainer.clientHeight) / 2);
                if (minimapSize > 40 && this.items.meta?.props?.minimap !== 'false') {
                    this.minimap.style.maxWidth = (minimapSize) + 'px';
                    this.minimap.style.maxHeight = (minimapSize) + 'px';
                    this.minimap.classList.remove('hidden');
                    this.minimap.handleScroll();
                    hideMinimap();
                }
            }, false);
            this.scrollContainer.addEventListener('scroll', this.showMinimap, {passive: true});
            hideMinimap();
            this.setDocument(document);
        }
        this.clearInternal();
        this[Symbol.dispose].defer(() => this.beforeUnloadDocument());
        this[Symbol.dispose].defer(() => this.clearInternal());
        if (this.interactive) {
            this.setupInteractive();
        }
    }

    get parent(): IHierarchy { return null; }
    get children(): IHierarchy[] { return Object.values(this.items); }

    setupInteractive() {
        this.scrollContainer.classList.add('interactive');
        this.scrollContainer.tabIndex = 0;
        this.container.addEventListener("drop", this.handleDropEvent.bind(this));
        this.container.addEventListener("dragover", this.handleDragOverEvent.bind(this));
        this.container.addEventListener("dblclick", this._containerDoubleClick);
        this.container.addEventListener('pointerdown', this._containerPointerDown);
        this.container.addEventListener('pointerup', this._containerPointerUp);
        this.container.addEventListener('pointercancel', () => this._clearRubberband(), true);
        this.scrollContainer.addEventListener("keydown", this.handleKeyEvent.bind(this), false);
        this.container.addEventListener('contextmenu', (e) => e.preventDefault(), true);
    }
    @BoundMethod()
    _containerDoubleClick(e: MouseEvent) {
        var position = this._getClientCoords(e);
        this._rubberbandStart(position);
        this._rubberbandUpdate(position.plus(new Point(224, 28)), null);
        if (!isTouchOnlyDevice) this.rubberbandStartPos = null;
        this.rubberbandRect = { x: parseInt(this.rubberband.style.left),y: parseInt(this.rubberband.style.top),
            width: parseInt(this.rubberband.style.width),height: parseInt(this.rubberband.style.height), };
        this.sel.deselectAll(false);
    }

    @BoundMethod()
    _containerPointerDown(e: PointerEvent) {
        this.sel.activeMemo = this;
        if (e.pointerType === 'touch') {
            if (this.rubberbandStartPos) {
                this._rubberbandPointerMove(e);
            } else {
                this._clearRubberband();
                this.sel.showAppropriateToolbars();
            }
        } else if (e.buttons === 1) {
            this._clearRubberband();
            if (e.buttons !== 1) return;
            if (!this.options.allowSelect) return;
            if (e.target === this.container || e.altKey || (e.target && (e.target as HTMLElement).closest('.clickthrough'))) {
                this._rubberbandStart(this._getClientCoords(e));
                this.container.setPointerCapture(e.pointerId);
                this.container.addEventListener('pointermove', this._rubberbandPointerMove, true);
            }
        } else if (e.buttons === 2) {
            const startPosX = e.pageX;
            const startPosY = e.pageY;
            const startScrollX = this.scrollContainer.scrollLeft;
            const startScrollY = this.scrollContainer.scrollTop;
            let captured = false;
            const moveEvent = (e) => {
                if (e.buttons !== 2) { upEvent(e); return; }
                if (!captured && (startPosX-e.pageX)**2 + (startPosY-e.pageY)**2 > 5) {
                    this.container.setPointerCapture(e.pointerId);
                    captured = true;
                    SearchableMenu.closeCurrentMenu();
                }
                if (captured) {
                    this.scrollContainer.scrollLeft = startScrollX - e.pageX + startPosX;
                    this.scrollContainer.scrollTop = startScrollY - e.pageY + startPosY;
                    e.preventDefault();
                }
            };
            const upEvent = (e) => {
                this.container.removeEventListener('pointermove', moveEvent, true);
                this.container.removeEventListener('pointerup', upEvent, true);
                this.container.removeEventListener('pointercancel', upEvent, true);
            };
            this.container.addEventListener('pointermove', moveEvent, true);
            this.container.addEventListener('pointerup', upEvent, true);
            this.container.addEventListener('pointercancel', upEvent, true);
        }
    }
    @BoundMethod()
    _containerPointerUp(e: PointerEvent) {
        if (this.rubberbandStartPos) {
            this._rubberbandPointerUp(e);
        } else {
            const memoElement = (e.target as HTMLElement).closest("[data-itemid]");
            if (memoElement) {
                const memoItem = this.items[memoElement.getAttribute('data-itemid')];
                memoItem._onClick(e);
            } else {
                this.sel.deselectAll(true);
            }
        }
    }

    @BoundMethod()
    _rubberbandPointerMove(e: PointerEvent) {
        if (!this.rubberbandStartPos) return;
        var position = this._getClientCoords(e);
        this._rubberbandUpdate(position, e);
        if (e.buttons === 1) this.container.setPointerCapture(e.pointerId);
        else this._rubberbandPointerUp(e);
    }
    @BoundMethod()
    _rubberbandPointerUp(e: PointerEvent) {
        if (!this.rubberbandStartPos) return;
        this.rubberbandStartPos = null;
        this.container.removeEventListener('mousemove', this._rubberbandPointerMove, true);
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey && !this.readonly) {
            this.makeSpace(this.rubberbandRect); return;
        }
        if (!e.ctrlKey) this.sel.deselectAll(false);
        var matches = Object.values(this.items).filter(x => doRectsIntersect(this.rubberbandRect, x.props) && !x.props.deleted);
        if (e.altKey) matches = [];
        if (matches.length > 0 || (+this.rubberbandRect.width < 20 && +this.rubberbandRect.height < 20) || this.readonly) {
            this._clearRubberband();
            this.sel.selectMultiple(matches);
        }
        this.sel.activeMemo = this;
    }
    @BoundMethod()
    _rubberbandStart(opts: PointType) {
        this.rubberbandStartPos = opts;
        this.rubberbandRect = { x: opts.x, y: opts.y, width: 0, height: 0 };
    }
    @BoundMethod()
    _rubberbandUpdate(cur: PointType, evt: MouseEvent) {
        var x1 = Math.min(this.rubberbandStartPos.x, cur.x), x2 = Math.max(this.rubberbandStartPos.x, cur.x);
        var y1 = Math.min(this.rubberbandStartPos.y, cur.y), y2 = Math.max(this.rubberbandStartPos.y, cur.y);
        if (evt && evt.shiftKey && !this.readonly) {
            if ((x2-x1) > (y2-y1)) {
                y1 = 0; y2 = this.container.clientHeight;
            } else {
                x1 = 0; x2 = this.container.clientWidth;
            }
            this.rubberband.style.background =  "rgba(255,255,255,0.5)";
        } else {
            this.rubberband.style.background = "";
        }
        this.rubberband.style.left=(this.rubberbandRect.x = x1)+"px";
        this.rubberband.style.top=(this.rubberbandRect.y = y1)+"px";
        this.rubberband.style.width=(this.rubberbandRect.width = x2-x1)+"px";
        this.rubberband.style.height=(this.rubberbandRect.height = y2-y1)+"px";
        this.rubberband.style.display="";
    }

    get readonly() {
        return this.document ? this.document.readonly : true;
    }
    @slot(() => Activity, ($: typeof Activity) => $.activityTypes)
    activityTypesRegistry(): ({ name: string, getConstructor(): Promise<typeof Activity> })[] {
        return this.validItems.filter(i => i.props.activityType).map(i => Object.assign(i, { name: i.getString('activityType', ''), getConstructor() { return i.getScriptResult(); } }));
    }
    @slot(() => DomWindow, ($: typeof DomWindow) => $.windowTypes)
    windowTypesRegistry(): ({ name: string, getConstructor(): Promise<typeof DomWindow> })[] {
        return this.validItems.filter(i => i.props.windowType).map(i => Object.assign(i, { name: i.getString('windowType', ''), getConstructor() { return i.getScriptResult(); } }));
    }
    getToolbarItemDefs(): Readonly<Command[]> {
        return this.validItems.filter(i => i.props.toolbar).map(i => i.toToolbarItemObject());
    }
    @slot('document', ($: IDocument) => $.updated)
    onRemoteItemUpdate(changeset: IDocumentCommit) {
        for (const item of changeset.items)
            this.onItemUpdated(item);
    }
    async _onMetaUpdate() {
        var meta = this.items['meta'];
        this.container.style.background = meta ? meta.getString('memoBackground', '') : '';
        if (meta?.getString('memoBackgroundImage', '')) {
            this.container.style.backgroundImage = "url(" + await UiContext.instance.blobStore.getMediaURL(meta.getString('memoBackgroundImage', '')) + ")";
        }
        this.container.style.width = (this.memoWidth = (meta?.getInt('memoWidth', 2800) || 2800)) + 'px';
        this.container.style.height = (this.memoHeight = (meta?.getInt('memoHeight', 2800) || 2800)) + 'px';
        this.minimap?.redraw();
        if (meta?.getString('docRenderMode', '')) {

        }
        this.showMinimap?.();
    }
    async runAutostart(items?: MemoItem[]) {
        const tag = /\bautostart\b/;
        if (!items) items = this.validItems;
        for(const item of items) {
            if (tag.test(item.getString('tags', ''))) {
                try {
                    await this.sel.toolbarClick(item.toToolbarItemObject(), null, []);
                } catch(ex) {
                    UiContext.instance.logError(`Autostart [${item.props.id}](?${this.documentId}#${item.props.id}) failed: ${ex}`);
                }
            }
        }
    }
    _clearRubberband() {
        if (this.rubberband) this.rubberband.style.display="none";
        this.rubberbandRect = null;
        this.rubberbandStartPos = null;
    }
    jumpToItem(itemId: string, focus = false) {
        this.sel.deselectAll();
        if (this.items[itemId]) {
            this.sel.select(this.items[itemId]);
            if (focus) {
                this.items[itemId].el.scrollIntoView();
                //this.scrollContainer.scrollLeft = Math.max(0, this.items[itemId].getInt('x', 0) - 100);
                //this.scrollContainer.scrollTop = Math.max(0, this.items[itemId].getInt('y', 0) - 100);

                this.focus();
            }
        } else {
            throw "failed to navigate to non-existant item id " + itemId;
        }
    }
    focus() {
        this.scrollContainer.focus({preventScroll:true});
    }
    getSyncTimestamp() {
        // XXX this is wrong if the newest change is a deletion that is not loaded anymore!!!
        return Object.values(this.items).reduce((p, c) => Math.max(p, c.getInt('mt', 0)), 0);
    }

    async setDocument(document: IDocument) {
        if (this.document && this.document !== document) await this.beforeUnloadDocument.asyncCollect();
        this.document = document;
        await this.reloadDocument();
    }
    async reloadDocument() {
        using _ = ProgressBar.show('loading document '+this.documentId);
        try {
            this.container.style.display = 'none';
            this.loadIndicator.style.display = 'block';
            this.clearInternal();
            if (this.document) {
                this.isInitialRender = true;
                if (DomWindowConfig.isMobileMode && this.document.documentId?.endsWith('/parkplatz')) UiContext.instance.markdown.disableSyntaxHighlight = true;
                const initialItems = Object.values(this.document.items);
                for (const info of initialItems) {
                    this.onItemUpdated(info);
                }
                this.isInitialRender = false;
                UiContext.instance.markdown.disableSyntaxHighlight = false;
                this.showMinimap?.();
                setTimeout(() => {
                    for (const item of initialItems) {
                        this.items[item.id].initDraggable();
                    }
                }, 1);
                await this.document.ready;
            }
            this.minimap?.redraw();
        } catch (err) {
            UiContext.instance.logError(`Loading document ${this.documentId} failed: ${err}`);
        } finally {
            this.container.style.display = 'block';
            this.loadIndicator.style.display = 'none';
        }
    }

    /**
     * loads an empty in-memory document
     */
    async clear() {
        await this.setDocument(new InMemoryDocument());
    }

    /**
     * resets all internal state.
     * called at creation, before loading a new document, and at disposal.
     * @private
     */
    private clearInternal() {
        for(const item of Object.values(this.items))
            item[Symbol.dispose]();
        this.items = {};
        this.container.innerHTML = "";
        this.rubberband = EL("div", { class:'clickthrough', appendTo: this.container,
            style:'position: absolute; z-index: 9999999999; outline: 2px dotted grey; display: none;' });
        this.undoStack = [];
        this.redoStack = [];
        this._onMetaUpdate();
    }
    public async applyFromDocument(document: IDocument | string, rejectKeys?: string[]): Promise<void> {
        if (this.readonly) throw new Error("Memo is readonly");
        if (typeof document === 'string') document = await UiContext.instance.db.getDocument(document);
        const reject = new Set(rejectKeys ?? ['ft', 'doneTs', 'startTs']);
        const t = this.document.transact();
        for(const item of Object.values(document.items)) {
            t.update(filterObject(item, ([k, v]) => !reject.has(k)));
        }
        await t.commit();
    }
    async _resizeImageIfNecessary(file:Blob, ignoreBelowFileSize = 256*1024): Promise<Blob> {
            if (file.size < ignoreBelowFileSize) {
                return null;
            }
            let newFile = await ImageTools.resize(file, {
                width: 1024, // maximum width
                height: 768 // maximum height
            });
            if (newFile.size < file.size)
                return newFile;
            else
                return null;
    }
    _loadBlobAsArrayBuffer(file:Blob): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            var fr = new FileReader();
            fr.addEventListener("loadend", () => {
                resolve(fr.result as ArrayBuffer);
            });
            fr.onerror = reject;
            fr.readAsArrayBuffer(file);
            UiContext.instance.log("importing file of "+Math.round(file.size/1024)+" kB");
        });
    };
    async uploadFile(file:File, props:PropertyObject): Promise<MemoItem> {
        props["image_orig_filename"] = file.name;
        if (file.type.indexOf("image") === 0) {
            var origMediaBin = new Uint8Array(await this._loadBlobAsArrayBuffer(file));
            var origMediaId = await UiContext.instance.blobStore.storeBlob(origMediaBin);

            var resizedImage = await this._resizeImageIfNecessary(file);
            if (resizedImage) {
                var resizedImageBin = new Uint8Array(await this._loadBlobAsArrayBuffer(resizedImage));
                var resizedMediaId = await UiContext.instance.blobStore.storeBlob(resizedImageBin);
                props["image"] = file.type + ':' + resizedMediaId;
                props["file"] = file.type + ':' + origMediaId;
            } else {
                props["image"] = file.type + ':' + origMediaId;
                props["file"] = file.type + ':' + origMediaId;
            }
            return this.newItem(Object.assign({
                width:180,height:140, text:""
            }, props));
        } else { //if (confirm("Upload non-image file "+file.name)) {
            UiContext.instance.toast("info", "Uploading non-image file "+file.name, 3000);
            var origMediaBin = new Uint8Array(await this._loadBlobAsArrayBuffer(file));
            var origMediaId = await UiContext.instance.blobStore.storeBlob(origMediaBin);
            props["file"] = file.type + ':' + origMediaId;
            return this.newItem(Object.assign({
                width:180,height:28, text:file.name, icon:"📁", textAlign: "left",
            }, props));
        }
    }

    /**
     * @deprecated use replaceItem/updateItem instead!
     * @param info
     * @param doSend
     * @param fullOverwrite
     */
    public setItem(info: PropertyObject, doSend: boolean = false, fullOverwrite = false) {
        console.warn("DEPRECATED: Memo.setItem: use replaceItem/updateItem instead");
        if (!doSend)
            throw new Error("Memo.setItem without doSend not supported any more");
        if (fullOverwrite)
            this.replaceItem(info);
        else
            this.updateItem(info);
    }

    /**
     * inserts a new item or replaces an existing item
     * @param props  PropertyObject, required to have an `id` field. item with specified id will
     *               contain exactly the given props
     */
    public replaceItem(props: PropertyObject) {
        this.document.transact().replace(props).commit();
    }

    /**
     * inserts a new item or updates an existing item
     * @param props  PropertyObject, required to have an `id` field. item with specified id will
     *               have at least the given props, but pre-existing properties which are not
     *               specified in this argument are kept
     */
    public updateItem(props: PropertyObject) {
        this.document.transact().update(props).commit();
    }

    private onItemUpdated(props: PropertyObject) {
        if (typeof props.id !== 'string') throw ("onItemUpdated called with missing id");
        let id = props['id'];
        let memoItem = this.items[id];
        if (!memoItem) {
            if (props.deleted == 1) return;
            memoItem = this.items[id] = new this.options.itemConstructor(this, id);
        }

        memoItem.render(props);
        if (props.id === 'meta') this._onMetaUpdate();
    }

    /**
     * creates a new item. if props contain an `id`, it will be ignored and a new id generated
     * @param newProps
     */
    public newItem(newProps: PropertyObject) {
        if (this.readonly) throw new Error("Memo is readonly");

        newProps = shallowClone(this.itemDefaultProps, newProps);
        if ('id' in newProps && 'document' in newProps && newProps.document !== this.documentId) newProps['ref'] = `${newProps.document}#${newProps.id}`;
        delete newProps["id"];
        this.document.transact().replace(newProps).commit();

        return this.items[newProps.id];
    }

    newItemFromRubberband(props:PropertyObject) {
        if (this.readonly) return;
        var el = this.newItem(Object.assign({}, props, this.rubberbandRect));
        this._clearRubberband();
        this.sel.select(el);
        return el;
    }
    redrawMinimap() {
        this.minimap?.redraw();
    }
    handleKeyEvent(e:KeyboardEvent) {
        this.sel.handleKeyEvent(e);
    }
    getDropEffect(e:DragEvent): "link" | "copy" | "none" | "move" {
        if (this.readonly) { return 'none'; }
        e.preventDefault(); // Necessary. Allows us to drop.
        if (this.sel.dragStartElement) {
            if (e.ctrlKey && e.shiftKey) return 'link';
            else if (e.ctrlKey || e.shiftKey || this.sel.selectedItems.some(item => item.props.template)) return 'copy';
            else return 'move';
        } else {
            return 'copy';  // See the section on the DataTransfer object.
        }
    }
    handleDragOverEvent(e:DragEvent) {
        if (this.readonly) { e.dataTransfer.dropEffect = 'none'; return; }
        e.preventDefault(); // Necessary. Allows us to drop.
        e.dataTransfer.dropEffect = this.getDropEffect(e);
    }
    handleDropEvent(e:DragEvent) {
        e.preventDefault();
        if (!e.dataTransfer) return;
        this.sel.activeMemo = this;
        const position = this._getClientCoords(e);
        const effect = this.getDropEffect(e);
        if (this.sel.dragStartElement) {
            var delta = position.minus(this.sel.dragStartElement.props).minus(this.sel.dragStartOffset);
            console.log(e.dataTransfer.dropEffect, this.sel.selectedItems.length+ " Item(s) Dropped on "+this.documentId,e);
            const newSelection = [];
            this.sel.selectedItems.slice().forEach((item:MemoItem) => {
                // TODO correctly handle placement if dragging from two different memos
                // best way should be using the same logic as for foreign drop (getBoundingClientRect for everything)
                if (effect === 'link') {
                    newSelection.push(this.newItem(Object.assign({}, this.itemDefaultProps, {
                        text: `[${item.props.title || `${item.props.text}`.split(/\n/)[0]}](?${item.parent.documentId}#${item.props.id})`,
                        x: item.getInt('x', 0) + delta.x, y: item.getInt('y', 0) + delta.y  })));
                } else if (effect === 'copy') {
                    var d : PropertyObject = shallowClone(item.props,
                        { x: item.getInt('x', 0) + delta.x, y: item.getInt('y', 0) + delta.y });
                    delete d["template"];
                    newSelection.push(this.newItem(d));
                } else if (item.parent != this) {
                    var n = this.newItem(shallowClone(item.props, 
                        { x: item.getInt('x', 0) + delta.x, y: item.getInt('y', 0) + delta.y }));
                    if (item.selected) newSelection.push(n);
                    item.delete();
                } else {
                    item.setProperties({ x: item.getInt('x', 0) + delta.x, y: item.getInt('y', 0) + delta.y });
                    newSelection.push(item);
                }
            });
            this.sel.selectedItems = newSelection;
            this.sel.dragStartElement=null;

            return;
        } else if (e.dataTransfer.getData(MIME_TYPE_ITEMS)) {
            this.handlePaste(JSON.parse(e.dataTransfer.getData(MIME_TYPE_ITEMS)), position);
            return;
        }
        let handledLinkDrop = false;
        Array.prototype.forEach.call(e.dataTransfer.items, x => {
            const type = x.type;
            x.getAsString(result=>{
                console.log("Drop: ",type,">>>"+result+"<<<");
                if (handledLinkDrop) return;
                if (type === 'text/x-moz-url') {
                    var data = result.split(/\n/);
                    this.handleLinkDrop(position.x, position.y, data[0], data[1]);
                    handledLinkDrop = true;
                }
                if (type === 'text/uri-list') {
                    var data = result.split(/\n/);
                    data.forEach((url, index) => this.handleLinkDrop(position.x, position.y + index*40, url, null));
                    handledLinkDrop = true;
                }
                /*result = result.replace(/\0/g, '');
                let m;
                if (type === 'text/html' && (m = result.match(/^<a href="([^"]+)">([^<]+)<\/a>$/))) {
                    this.handleLinkDrop(position, m[1], m[2]);
                }*/
            })
        });
        for (var x=0; x < e.dataTransfer.files.length; x++) {
            try{
                this.uploadFile(e.dataTransfer.files[x], Object.assign({
                    x:position.x-75, y:position.y-60,
                }, this.rubberbandRect)).catch((e) => { UiContext.instance.logError("upload failed ",e); });
            }catch(e) { UiContext.instance.logError("upload failed ",e); }
        }
    }
    handlePaste(json: any[], forcePositionDelta?: PointType) {
        var delta : PointType = Point.zero;
        if (forcePositionDelta) {
            delta = forcePositionDelta;
        } else if (this.rubberbandRect) {
            delta = Point.min(...json).negate().plus(this.rubberbandRect);
        }
        this.sel.deselectAll();
        this.sel.selectMultiple(json.map(jsonItem => {
            jsonItem.x += delta.x; jsonItem.y += delta.y;
            return this.newItem(jsonItem);
        }));
    }
    handleLinkDrop(x, y, url, title) {
        if (title) {
            this.newItemFromRubberband({x, y, text: "[" + title + "](" + url + ")"});
        } else {
            const el = this.newItemFromRubberband({x, y, text: url});
            getTitleOfURL(url).then((title) => {
                if (title)
                    el.setProperties({ text: "[" + title + "](" + url + ")" });
            })
        }
    }
    selectAll() {
        this.sel.selectedItems = this.validItems;
    }
    /*
    getElementsInDirection(el:MemoItem, dir, breakDistance = null) {
        let left = (+el.props.x), right = (+el.props.x) + (+el.props.width), curBottom = (+el.props.y) + (+el.props.height);
        const bottom = (+el.props.y) + (+el.props.height*3/4);
        const els = [];
        for(const i of this.validItems.sort((a,b) => a.getInt("y", 0) - b.getInt("y", 0))) {
            if ((+i.props.y) > bottom && (+i.props.x) < right && (+i.props.x)+(+i.props.width) > left) {
                if (breakDistance && curBottom + breakDistance < +i.props.y) break;
                els.push(i);
                left = Math.min(left, +i.props.x);
                right = Math.max(right, +i.props.x + (+i.props.width));
                curBottom = (+i.props.y) + (+i.props.height);
            }
        }
        return els;
    }
    */
    getElementsBelow(el:MemoItem, breakDistance = null) {
        let left = (+el.props.x), right = (+el.props.x) + (+el.props.width), curBottom = (+el.props.y) + (+el.props.height);
        const bottom = (+el.props.y) + (+el.props.height*3/4);
        const els = [];
        for(const i of this.validItems.sort((a,b) => a.getInt("y", 0) - b.getInt("y", 0))) {
            if ((+i.props.y) > bottom && (+i.props.x) < right && (+i.props.x)+(+i.props.width) > left) {
                if (breakDistance && curBottom + breakDistance < +i.props.y) break;
                els.push(i);
                left = Math.min(left, +i.props.x);
                right = Math.max(right, +i.props.x + (+i.props.width));
                curBottom = (+i.props.y) + (+i.props.height);
            }
        }
        return els;
        //return this.items.filter((i) => i.props.y > bottom && i.props.x < right && i.props.x+i.props.width > left);
    }
    makeSpace(rect: {x:number,y:number,width:number,height:number}) {
        if (rect.x) {
            this.validItems.filter(item => item.rect?.x > rect.x).forEach(item => item.setProperties({ x: item.rect?.x + rect.width }));
        } else if (rect.y) {
            this.validItems.filter(item => item.rect?.y > rect.y).forEach(item => item.setProperties({ y: item.rect?.y + rect.height }));
        }
    }
    _snapToGrid(value:number) : number {
        return value - ((value+2) % 4 - 2);
    }
    _getClientCoords(event: MouseEvent): Point {
        var bounding = this.container.getBoundingClientRect();
        return new Point(event.clientX - bounding.x + this.container.scrollLeft,
                         event.clientY - bounding.y + this.container.scrollTop);
    }
    _getScreenCoords(props : PropertyObject): Point {
        var bounding = this.container.getBoundingClientRect();
        return new Point((+props.x) + bounding.x - this.container.scrollLeft,
                         (+props.y) + bounding.y - this.container.scrollTop);
    }
    getOverlap(items, z, zz) {
        items.sort((a,b) => a.props[z] - b.props[z]);
        let overlap = 0;
        for(let i = 0; i < items.length - 2; i++) {
            overlap += Math.max(0, Math.min(items[i].props[z] + items[i].props[zz], items[i + 1].props[z] + items[i + 1].props[zz]) - Math.max(items[i].props[z], items[i + 1].props[z]));
        }
        return overlap / items.length;
    }
    alignItems(param: string, _1, _2, itemList:MemoItem[]) {
        if (param == 'left') {
            itemList.forEach(item => item.setProperties({ x: (+itemList[0].props.x) }));
        } else if (param == 'right') {
            itemList.forEach(item => item.setProperties({ x: (+itemList[0].props.x) + (+itemList[0].props.width) - (+item.props.width) }));
        } else if (param == 'top') {
            itemList.forEach(item => item.setProperties({ y: (+itemList[0].props.y) }));
        } else if (param == 'bottom') {
            itemList.forEach(item => item.setProperties({ y: (+itemList[0].props.y) + (+itemList[0].props.height) - (+item.props.height) }));
        } else if (param == 'width') {
            if (this.getOverlap(itemList, 'x', 'width') >= this.getOverlap(itemList, 'y', 'height')) {
                this.alignItems('xx', _1, _2, itemList);
            } else {
                this.alignItems('row', _1, _2, itemList);
            }
        } else if (param == 'xx') {
            itemList.forEach(item => item.setProperties({ x: (+itemList[0].props.x), width: (+itemList[0].props.width) }));
        } else if (param == 'row') {
            itemList.sort((a,b) => +a.props.x - +b.props.x);

            let pos = itemList[0].getInt('x', 0);
            const top = itemList[0].getInt('y', 0);

            for(const el of itemList) {
              el.setProperties({x: pos, y: top});
              pos = el.getInt('x', 0) + el.getInt('width', 0) + el.getInt('paddingRight', 4);
            }
        } else if (param == 'height') {
            if (this.getOverlap(itemList, 'y', 'height') >= this.getOverlap(itemList, 'x', 'width')) {
                this.alignItems('yy', _1, _2, itemList);
            } else {
                this.alignItems('column', _1, _2, itemList);
            }
        } else if (param == 'yy') {
            itemList.forEach(item => item.setProperties({ y: (+itemList[0].props.y), height: (+itemList[0].props.height) }));
        } else if (param == 'column') {
            itemList.sort((a,b) => +a.props.y - +b.props.y);

            let pos = itemList[0].getInt('y', 0);
            const left = itemList[0].getInt('x', 0);

            for(const el of itemList) {
              el.setProperties({y: pos, x: left});
              pos = el.getInt('y', 0) + el.getInt('height', 0) + el.getInt('paddingBottom', 4);
            }
        }
    }
    addUndoStep(props: PropertyObject) {
        const copy = shallowClone(props);
        this.undoStack.push(copy);
        this.redoStack = [];
        if (this.undoStack.length > 100) this.undoStack.unshift();
    }
    undo() {
        if (this.undoStack.length == 0) return;
        var props : PropertyObject = this.undoStack.pop()!!;
        this.redoStack.push(shallowClone(this.items[props.id].props));
        this.replaceItem(props);
    }
    redo() {
        if (this.redoStack.length == 0) return;
        var props : PropertyObject = this.redoStack.pop()!!;
        this.undoStack.push(shallowClone(this.items[props.id].props));
        this.replaceItem(props);
    }
    copyToClipboard(_1, _2, _3, itemList:MemoItem[]) {
        var obj = itemList.map(x => x.props);
        var json = JSON.stringify(obj);
        navigator.clipboard.writeText(json);
        this.sel.lastCopiedToClipboard = window['lastCopiedToClipboard'] = structuredClone(obj);
    }
    async chooseEmoji(_1, _2, _3, itemList:MemoItem[]) {
        const editorWindow = await import('./cm/EditorWindow.js');
        editorWindow.showEmojiPicker($(itemList[0].el).offset(), (emoji) => {
            itemList.forEach((item) => {
                item.setProperties({ icon: emoji.icon });
            })
        });
    }
    print(itemList:MemoItem[]) {
        const itemProps = itemList.map(x => x.props);
        var min = Point.min(...itemProps);
        var wnd = window.open();
        wnd.document.write("<link rel=stylesheet href='/style.css'><style>html,body{overflow:inherit;}</style><div id='memoArea'></div>");
        var newMemo = new Memo(wnd.document.getElementById('memoArea'), null, new InMemoryDocument(), {
            interactive: false
        });
        for (const props of itemProps) newMemo.replaceItem(shallowClone(props, {x: +props.x - min.x, y: +props.y - min.y }));
    }
}

function doRectsIntersect(rect1: PropertyObject, rect2: PropertyObject) {
    if ((+rect1.x) < (+rect2.x) + (+rect2.width) && (+rect2.x) < (+rect1.x) + (+rect1.width) && (+rect1.y) < (+rect2.y) + (+rect2.height))
        return (+rect2.y) < (+rect1.y) + (+rect1.height);
    else
        return false;
}

const id = (x) => x;


export class MemoItem implements Disposable, IHierarchy {
    private _selected = false;
    get selected(): boolean {
        return this._selected;
    }
    set selected(value: boolean) {
        this._selected = value;
        if (this.contentEl) this.contentEl.style.outline = value ? this.parent.options.selectedOutlineStyle : "";
        if (this.el) this.el.classList.toggle('selected', value);
        if (this.el) this.el.style.touchAction = value ? 'none' : '';
    }

    private _props:Readonly<PropertyObject>;
    get props(): Readonly<PropertyObject> { return this._props };

    el:HTMLElement;
    contentEl:HTMLElement;
    iconEl:HTMLElement;
    footerEl: HTMLElement;
    touchMoveStartX:number;
    touchMoveStartY:number;
    touchDownTimestamp:number;
    touchHoldTimer:number | null;
    touchMode:string = '';
    script: ScriptHost;
    disposableStack = new DisposableStack();
    rect : DOMRect;
    children: IHierarchy[];
    constructor(public parent:Memo, id:string) {
        this._props = Object.freeze({ "id": id });
    }
    [Symbol.dispose](): void {
        this.resetScript();
        this.disposableStack?.dispose();
        if (this.selected) this.parent.sel.deselect(this);
    }
    update(info:PropertyObject) {
        if ('id' in info && info.id !== this._props.id) throw "MemoItem id update not allowed";
        info.id = this._props.id;
        this.parent.document.transact().update(info).commit();
    }
    getCodeBlock(id: string) {
        return this.getScript().getCodeBlockById(id);
    }
    private static _parsePropsFromText(text: string) {
        let props = {};
        UiContext.instance.markdown.render(text, false, props);
        /*
            const codeBlocks = ScriptHost.getAllCodeBlocks(text);
            for (const { id, lang, attrs, code, kv } of codeBlocks) {
                if (id === 'js:main') {
                    if (kv.typereg && kv.class) {
                        props[kv.typereg + 'Type'] = kv.class;
                    }
                }
            }
        */
        return props;
    }
    private static _updatePropsFromText(oldText: string, newText: string, dest: object) {
        let oldProps = this._parsePropsFromText(oldText);
        let newProps = this._parsePropsFromText(newText);
        let propDiff = compareObjects(oldProps, newProps);
        for(const {op, k} of propDiff) {
            if (op === 'd') dest[k] = undefined;
            else dest[k] = newProps[k];
        }
    }
    setProperties(info:PropertyObject, setIfMissing:PropertyObject = null) {
        if (this.parent.readonly) throw new Error("Memo is readonly");
        this.parent.addUndoStep(this.props);
        if (!this._props['ft'] && !('ft' in info)) info['ft'] = Date.now();
        const snap = ['x', 'y', 'width', 'height'];
        for(const v of snap)
            if (v in info) info[v] = this.parent._snapToGrid(+info[v]);
        if (setIfMissing) {
            for(const k in setIfMissing) {
                if (!this._props[k] && !info[k]) info[k] = setIfMissing[k];
            }
        }
        if ('text' in info) {
            MemoItem._updatePropsFromText(this.getString('text', ''), info.text as string, info);
        }
        console.log(this.props.id, "<=", JSON.stringify(info));
        info.id = this._props.id;
        this.parent.document.transact().update(info).commit();
        if (this.selected) this.parent.sel.showAppropriateToolbars();
    }
    cycleProperty(propName:string, values:(string|number)[]) {
        var idx = propName in this._props ? values.indexOf(""+this._props[propName]) : -1;
        if (idx == values.length - 1) idx = -1;
        this.setProperties({ [propName]: values[idx+1] });
    }
    cyclePropertyBackwards(propName:string, values:(string|number)[]) {
        var idx = propName in this._props ? values.indexOf(""+this._props[propName]) : -1;
        if (idx == 0) idx = values.length;
        if (idx == -1) idx = 1;
        this.setProperties({ [propName]: values[idx-1] });
    }
    getWithDefault(prop:string, defaultValue:string|number) : any {
        if (prop in this._props && this._props[prop] !== null)
            return this._props[prop];
        else return defaultValue;
    }
    getInt(prop:string, defaultValue:number) : number {
        if (prop in this._props && !isNaN(parseInt(this._props[prop] as string)))
            return parseInt(this._props[prop] as string);
        else return defaultValue;
    }
    getFloat(prop:string, defaultValue:number) : number {
        if (prop in this._props && !isNaN(parseFloat(this._props[prop] as string)))
            return parseFloat(this._props[prop] as string);
        else return defaultValue;
    }
    getString(prop:string, defaultValue:string) : string {
        if (prop in this._props && this._props[prop] !== null)
            return `${this._props[prop]}`;
        else return defaultValue;
    }
    initDraggable() {
        if (isTouchOnlyDevice || !this.parent.interactive || !this.el || this.getString('nomove', '')) return;
        $(this.el).resizable({
            autoHide: true,
            stop: ( event, ui ) => {
                this.setProperties({ width: parseInt(this.el.style.width), height: parseInt(this.el.style.height) });
            },
            disabled: !!(!this.parent.interactive || this.getString('nomove', '') || this.parent.readonly),
            grid: [4,4],
        });
        this.el.setAttribute('draggable', 'true');
        this.el.addEventListener('dragstart', (e) => {
            if (e.altKey) return false;
            if (!this.parent.interactive) return false;
            if (!this.selected) { this.parent.sel.deselectAll(); this.parent.sel.selectMultiple([ this ], true); }
            const items = this.parent.sel.selectedItems;
            if (items.some(x => x.getString('nomove', ''))) return false;
            e.dataTransfer.setData("text/plain", items.map(x => x.props.text).join("\n"));
            e.dataTransfer.setData(MIME_TYPE_ITEMS, JSON.stringify(items.map(x => {
                const rect = this.el.getBoundingClientRect();
                return shallowClone(x.props, { x: rect.x - e.clientX, y: rect.y - e.clientY })
            })));
            var allowMove = !items.some(x => x.parent.readonly);
            var allowLink = items.every(x => x.parent.documentId);
            if (allowMove) {
                this.parent.sel.dragStartElement = this;
                const rect = this.el.getBoundingClientRect();
                this.parent.sel.dragStartOffset = new Point(e.clientX - rect.x, e.clientY - rect.y);
            }

            e.dataTransfer.effectAllowed = effectAllowed(allowMove, allowLink);
            e.dataTransfer.dropEffect = 'copy';
            this.parent.sel.clearToolbars();
        });
        this.el.addEventListener('dragend', (e) => {
            this.parent.sel.dragStartElement = null;
        });
    }
    _onClick(e: MouseEvent) {
        if(e.altKey)return true;
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' && target.getAttribute('type') === 'checkbox' && target.closest('li.task-list-item')) {
            this.toggleTaskListItem(parseInt(target.getAttribute('id').substring(4)));
            return;
        }
        if (e.metaKey || e.ctrlKey) {
            if (this.selected) this.parent.sel.deselect(this);
            else this.parent.sel.select(this);
        } else {
            this.parent.sel.deselectAll();
            this.parent.sel.select(this);
        }
        //this.parent.focus();
    }
    initElement(noDraggable = false) {
        this.el = EL('div', { 'data-itemid': this.props.id as string, style: 'position: absolute;' },
            this.iconEl = EL('div', { style: 'position: absolute' }),
            this.contentEl = EL('div', { style: 'width: 100%; height: 100%; overflow: hidden;' }),
            this.footerEl = EL('div', { class: 'memoItemFooter' })
        );
        
        this.parent.container.appendChild(this.el);
        
        if (!isTouchOnlyDevice && !noDraggable && this.parent.interactive && !this.parent.isInitialRender) this.initDraggable();
        if (this.parent.interactive || this.parent.options.alwaysAllowIconClick) {
            this.iconEl.addEventListener("click", this.onIconClick.bind(this));
        }
        if (this.parent.interactive) {
            this.el.addEventListener('touchstart', (e) => {
                if (this.selected && !this.getString('nomove', '')) {
                    e.preventDefault();
                    if (e.touches.length != 1) return;
                    this.touchMoveStartX = e.touches[0].screenX;
                    this.touchMoveStartY = e.touches[0].screenY;
                    this.touchDownTimestamp = Date.now();
                    this.contentEl.style.outline = "6px dotted #f0f";
                    this.touchMode = 'shortpress';
                    this.touchHoldTimer = window.setTimeout(()=> { this.contentEl.style.outline = "6px dotted #0f0"; if (this.touchMode === 'shortpress') this.touchMode = 'longpress'; }, LONG_PRESS_TIMEOUT);
                }
            }, {passive: true});
            this.el.addEventListener('touchmove', (e) => {
                if (this.selected) {
                    e.preventDefault();
                    if (e.touches.length != 1) return;
                    if ((e.touches[0].screenX - this.touchMoveStartX)**2 + (e.touches[0].screenY - this.touchMoveStartY)**2 > 5) {
                        if (this.touchMode === 'shortpress') this.touchMode = 'move';
                        if (this.touchMode === 'longpress') this.touchMode = 'resize';
                        if (this.touchHoldTimer)window.clearTimeout(this.touchHoldTimer);
                        this.touchHoldTimer=null;
                    }
                    if (this.touchMode === 'move') {
                        this.el.style.left = this.parent._snapToGrid(e.touches[0].screenX - this.touchMoveStartX + +this.props.x) + "px";
                        this.el.style.top = this.parent._snapToGrid(e.touches[0].screenY - this.touchMoveStartY + +this.props.y) + "px";
                    } else if (this.touchMode === 'resize') {
                        this.el.style.width = this.parent._snapToGrid(e.touches[0].screenX - this.touchMoveStartX + +this.props.width) + "px";
                        this.el.style.height = this.parent._snapToGrid(e.touches[0].screenY - this.touchMoveStartY + +this.props.height) + "px";
                    }
                }
            }, {passive: true});
            this.el.addEventListener('touchend', (e) => {
                if (this.selected) {
                    if(this.touchHoldTimer)clearTimeout(this.touchHoldTimer);
                    this.touchHoldTimer=null;
                    e.preventDefault();
                    this.contentEl.style.outline = "3px dashed #00f";
                    if(this.touchMode === 'shortpress') {
                        if (e.target === this.iconEl) this.onIconClick(e);
                    } else if(this.touchMode === 'longpress') {
                        this.contextMenu(e);
                    } else if (this.touchMode === 'move') {
                        var newX = parseInt(this.el.style.left);
                        var newY = parseInt(this.el.style.top);
                        if (Math.abs(+this.props.x - newX) > 3 || Math.abs(+this.props.y - newY) > 3) {
                            this.setProperties({ x: newX, y: newY });
                        }
                    } else if (this.touchMode === 'resize') {
                        var newWidth = parseInt(this.el.style.width);
                        var newHeight = parseInt(this.el.style.height);
                        this.setProperties({ width: newWidth, height: newHeight });
                    }
                }
                this.touchMode = '';
            });
            $(this.el).dblclick((e) => {
                this.parent.sel.handleHotkeyByName('DblClick', null, [this]);
                return false;
            }).mouseup((e)=>{
                if (e.target.tagName == "A") return;
                if(e.which==3 && !e.altKey && !e.ctrlKey) {
                    if (!this.selected) {
                        this.parent.sel.deselectAll(); this.parent.sel.select(this);
                    } 
                    e.preventDefault();
                }
            }).contextmenu((e)=>{
                if (e.target.tagName == "A") return;
                if (isTouchOnlyDevice && this.parent.sel.selectedItems.length !== 0) {
                    e.preventDefault();
                    this.select();
                } else if (!e.altKey && !e.ctrlKey) {
                    e.preventDefault();
                    this.contextMenu(e);
                }
            });
        }
    }
    toggleTaskListItem(index: number) {
        let counter = 0;
        let text = this.getString('text', '').replace(/^(\s*[-*]\s+\[)([ xX])(\]\s+)/mg, function(full, before, x, after) {
            return (counter++ == index) ? before + (x === ' ' ? 'X' : ' ') + after : full;
        });
        this.setProperties({ text });
    }
    delete() {
        if (this.selected) this.parent.sel.deselect(this);
        this.setProperties({ deleted: 1 });
        UiContext.instance.toast("toast", `Item ${this._props.id} deleted.`, 4500, [["Undo", () => this.parent.undo()]]);
    }
    get isDeleted(): boolean {
        return !!this.props.deleted;
    }
    select() {
        this.parent.sel.select(this);
    }
    async editProperties() {
        ObjectProperties; // make sure file gets referenced
        MemoItemProperties; // make sure file gets referenced
        const propertyEditor = MemoItemProperties.for(this);
        propertyEditor.show();
        setTimeout(() => propertyEditor.focus(), 0);
    }
    async contextMenu(event) {
        const targetItems = this.selected ? this.parent.sel.selectedItems : [this];
        const menuItems = await this.parent.sel.buildContextMenu('itemctx', targetItems, event, 'MEMOITEM');
        try {
            this.el.classList.add("contextmenuOpen");
            await ShowDropdown(event, menuItems);
        } finally {
            this.el.classList.remove("contextmenuOpen");
        }
    }
    renderText(str: string) {
        switch(this.props.textMode) {
            case 'plain':
                this.contentEl.textContent = str;
                break;
            case 'html':
                this.contentEl.innerHTML = UiContext.instance.markdown.sanitize(str);
                break;
            case 'csv':
                this.contentEl.innerHTML = Markdown.csvToHtml(str, this.props.csvOpts || {fSep: '\t', head: true});
                break;
            case 'md': default:
                this.contentEl.innerHTML = UiContext.instance.markdown.render(str, false);
                break;
        }
        if (this.props.title) this.contentEl.prepend(EL('h4', {class:'memoItemTitle'}, this.getString('title', '')));
    }
    renderFooter() {
        let showFooter = "";
        const tags = this.getString('tags','');
        if (this.getInt('lmt',0) > this.getInt('mt', 0)) {
            showFooter += "[needs-sync] ";
        }
        if (this.getInt('doneTs',0) > 0) {
            showFooter += "[done: "+new Date(this.getInt('doneTs',0)).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: false}) +"] ";
        } else if (this.getInt('startTs',0) > 0) {
            showFooter += "[start: "+new Date(this.getInt('startTs',0)).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: false}) +"] ";
        }
        if (tags) {
            showFooter += tags.split(/\s+/).map(tag => `[#${tag}] `).join('');
        }
        this.footerEl.innerText += showFooter;
    }
    refreshElement() {
        if (this.el) { $(this.el).remove(); this.el=null; }
        this.render(this.props, {});
    }
    render(newProps: PropertyObject, oldProps?: PropertyObject) {
        if (!oldProps) oldProps = this._props;
        this._props = Object.freeze(newProps);
        if (this.isDeleted || !(typeof this.props.x === 'number' && typeof this.props.y === 'number' && typeof this.props.width === 'number' && typeof this.props.height === 'number')) {
            if (this.el) { this.resetScript(); this.script = null; $(this.el).remove(); this.el=null; this.contentEl=null; }
            this.rect = null;
            return;
        }
        if (!this.el) {
            this.initElement();
        }
        this.el.className=[
            'memoItem',
            `textmode-${this.getString('textMode', 'md')}`,
            this.getString('class', ''),
            (this.getString('nosel', '') && this.getString('nomove', '')) ? 'clickthrough' : ''
        ].join(' ');
        this.el.style.border = this.getString("border", "1px solid black");
        this.el.style.backgroundSize = this.getString("backgroundSize", "contain");
        this.el.style.backgroundRepeat = this.getString("backgroundRepeat", "no-repeat");
        this.el.style.backgroundPosition = this.getString("backgroundPosition", "");
        this.el.style.printColorAdjust = 'exact';
        if (this.props.image) {
            UiContext.instance.blobStore.getMediaURL(this.getString('image', '')).then(
                (url) => this.el && (this.el.style.backgroundImage="url('"+url+"')"));
        } else
            this.el.style.backgroundImage="none";
        this.el.style.backgroundColor = this.getString("backgroundColor", "rgba(200,200,200,0.4)");
        this.contentEl.style.color = this.getString("textColor", "black");
        this.contentEl.style.fontSize = this.getWithDefault("textSize", "10") + "pt";
        this.contentEl.style.fontFamily = this.getString("font", "");
        //this.el.style.zIndex = this.getWithDefault("zIndex", "1000");
        this.contentEl.style.padding = this.getWithDefault("padding", "2") + "px";
        this.iconEl.style.padding = this.getWithDefault("padding", "2") + "px";
        if (this.parent.interactive || this.parent.options.alwaysAllowIconClick) {
            this.iconEl.style.cursor = this.hasClickAction() ? "pointer" : "default";
            this.iconEl.onmousedown = this.hasClickAction() ? function(e) { e.stopPropagation(); } : null;
        }
        var icon = this.getString("icon", "");
        var iconSize = this.getFloat("iconSize", 40);
        var calculatedIconSize = Math.min(iconSize, +this.props.height-6);
        var actualIconSize = renderIcon2(icon, calculatedIconSize, UiContext.instance.blobStore, this.iconEl);
        if (actualIconSize) this.contentEl.style.paddingLeft = actualIconSize+8+"px";
        
        this.contentEl.style.textAlign = this.getString("textAlign", "center");
        this.rect = DOMRect.fromRect(this.props as any);
        this.el.style.left = this.props.x + "px";
        this.el.style.top = this.props.y + "px";
        this.el.style.width = this.props.width + "px";
        this.el.style.height = this.props.height + "px";
        if (oldProps.text != this.props.text || oldProps.textMode != this.props.textMode || oldProps.title != this.props.title || !deepEqual(oldProps.sig, this.props.sig)) {
            this.resetScript(); this.script = null;
            this.renderText(this.getString("text", ""));
            //MathJax.Hub.Queue(["Typeset",MathJax.Hub,this.contentEl]);
            if (this.props.autosize) {
                this.el.style.height='auto';
                if (Math.abs(this.contentEl.clientHeight - +this.props.height) > 8){
                    console.log("autosize update", this.contentEl.clientHeight , +this.props.height);
                    this.setProperties({height: this.contentEl.clientHeight});
                }
            }
        }
        this.contentEl.style.outline = this._selected ? "3px dashed #00f" : "";
        this.el.classList.toggle('selected', this._selected);
        this.footerEl.innerText = '';
        this.renderFooter();
    }
    async editText(editorOptions?): Promise<void> {
        const editor = DomWindow.windows['editor'] as IEditorWindow;
        if (!editor) {
            UiContext.instance.toast('error', 'Editor not loaded yet, please try again...', 3000);
            return;
        }
        return Promise.resolve().then(async () => {
            await editor.setItem(this, editorOptions);
            if (!editor.$wnd.is(":visible") || !editor.wasMoved) {
                var pos = this.parent._getScreenCoords(this.props);
                if (this.getString('text','').split(/\n/).length > this.getInt('height',0)/20 || this.getInt('height',0) > window.innerHeight - 70)
                    editor.setBounds({x:2,xx:window.innerWidth-4,y:2,yy:window.innerHeight-4});
                    //editor.setBounds({x:190,xx:window.innerWidth-400,y:80,yy:window.innerHeight-150});
                else
                    editor.setBounds({x:pos.x, y:pos.y-15, xx:Math.max(300,+this.props.width), yy:Math.max(80, (+this.props.height)+30+(this.props.title?30:0))});
            }
            editor.show();
        });
    }

    static copyPropsBelow = {
        'border': id, 'backgroundColor': id, 'backgroundSize': id, 'backgroundRepeat': id, 'image': id, 'textColor': id, 'textSize': id, 
        'icon': (icon) => (icon == 'checked.svg' || icon == '✅' || icon == '🔛' || icon == '🔜' || icon == '❎') ? 'unchecked.svg' : icon,
        'zIndex': id, 'padding': id, 'textAlign': id, 'iconSize': id, 'class': id,
    };
    insertItemBelowMe(moveItemsBelow) {
        var itemsToMove = this.parent.getElementsBelow(this);
        var newProps : PropertyObject = { x: this.getInt('x', 0), y: this.getInt('y', 0) + this.getInt('height', 0) + this.getInt('paddingBottom', 4), width: this.getInt('width', 0), height: this.getInt('height', 0) };
        for(const key in this.props) if (MemoItem.copyPropsBelow[key]) newProps[key] = MemoItem.copyPropsBelow[key].call(this, this.props[key]);
        var newItem = this.parent.newItem(newProps);
        if (moveItemsBelow)
            itemsToMove.forEach((i) => i.setProperties({ y: i.getInt('y', 0) + newItem.getInt('height', 0) + this.getInt('paddingBottom', 4) }));
        this.parent.sel.setSelection(newItem);
        return newItem;
    }
    hasClickAction() {
        return (this.props.icon === '🟩'
         //|| this.props.icon === '✅'
         || this.props.icon === '🟦'
         || this.props.icon === 'unchecked.svg'
         //|| this.props.icon === '☑️'
         || this.props.icon === '🔜'
         || this.props.icon === '🔛'
         //|| this.props.icon === '➡️'
         || this.props.icon === 'check_box_uncheck.png'
         || this.props.icon === 'check_box.png'
         || this.props.file
         || this.props.click || this.props.clickItemMethod || this.props.clickSelectionMethod || this.props.clickMemoMethod || this.props.clickAppMethod || this.props.clickSelfMethod);
    }
    async onIconClick(e: Event) {
        if (this.props.icon === '🟩' || this.props.icon === '🟦' || this.props.icon === 'unchecked.svg')
            this.setProperties({'icon': '🔛','_icon':this.props.icon}, {startTs: Date.now()});
        else if (this.props.icon === '🔜')
            this.setProperties({'icon': '🔛'});
        else if (this.props.icon === '🔛')
            this.setProperties({'icon': 'checked.svg', '_icon':undefined}, {doneTs: Date.now()});

        else if (this.props.icon === 'check_box_uncheck.png')
            this.setProperties({'icon': 'check_box.png'}, {doneTs: Date.now()});
        else if (this.props.icon === 'check_box.png')
            this.setProperties({'icon': 'check_box_uncheck.png'});
        else if (this.props.file) {
            window.open(await UiContext.instance.blobStore.getMediaURL(this.getString('file', '')));
        } else if (this.props.click || this.props.clickItemMethod || this.props.clickSelectionMethod || this.props.clickMemoMethod || this.props.clickAppMethod || this.props.clickSelfMethod) {
            this.parent.sel.toolbarClick(this.toToolbarItemObject(), null);
        } else {
            if (!this.parent.sel.handleHotkeyByName('IconClick', null, [this])) return;
        }
        if (e) e.stopPropagation();
        return false;
    }
    toToolbarItemObject(): Command {
        try {
            return Command.fromProperties(this.props, this);
        } catch(e) {
            UiContext.instance.logError(`invalid toolbar item defined in [${this.parent.documentId}#${this.props.id}](?${this.parent.documentId}#${this.props.id}): ${e}`, e);

            const fakeCmd = new Command({}, this);
            Object.assign(fakeCmd.props, this.props);
            fakeCmd.props.icon ="⚠️";
            return fakeCmd;
        }
    }
    async getBlob(): Promise<Blob> {
        var mediaId = this.getString('image', '') ?? this.getString('file', '');
        if (!mediaId) throw "Item has no media attribute";
        return await UiContext.instance.blobStore.retrieveBlobEx(mediaId);
    }
    async copyBlobToClipboard() {
        var blob = await this.getBlob();
        if (!('ClipboardItem' in window)) throw "Please enable 'dom.events.asyncClipboard.clipboardItem' in about:config";
        navigator.clipboard.write([new ClipboardItem({
            [blob.type]: blob
        })]);
    }
    async changeItemIdWithDialog() {
        if (this.parent.readonly) throw new Error("Memo is readonly");
        var newId = prompt("Change Item ID", this.props.id as string);
        if (!newId || newId == this.props.id) return;
        var newProps = Object.assign({}, this.props, {id: newId});
        this.parent.updateItem(newProps);
        this.delete();
        return newId;
    }


    resetScript() {
        this.script?.resetScript();
    }
    getScript(): ScriptHost {
        if (!this.script) {
            this.script = new ScriptHost(this.parent.documentId + '#' + this.props.id, this.getString('text', ''), this.props.sig as any, this, { mu: this.getString('mu', ''), mt: this.getInt('mt', 0) });
            this['hook'] = this.script.hook.bind(this.script);
            this['slot'] = this.script.slot.bind(this.script);
        }
        return this.script;
    }
    evalScriptContents(param: any, param2: any, event: any, target?: any): Promise<void> {
        return this.getScript().evalScriptContents(param, param2, event, target);
    }
    getScriptResult(): Promise<any> {
        return this.getScript().getScriptResult();
    }
    get contentDisposableStack(): DisposableStack {
        return this.script.contentDisposableStack;
    }

/*
    slot(object, signalName, handler, callWithArgs?: any[]) { throw 'mixin?!'; }
    hook(object, method, handler, callWithArgs?: any[]) {throw 'mixin?!'; }
    resetScript(): void { throw 'mixin?!'; }
    getScriptContents(): string { throw 'mixin?!'; }
    getExecutableScript(): { lang: string; code: string; attrs: string; } { throw 'mixin?!'; }
    getAllCodeBlocks(): { lang: string; code: string; attrs: string; }[] { throw 'mixin?!'; }
    isScriptTrusted(script: any): Promise<boolean> { throw 'mixin?!'; }
    evalScriptContents(param: any, param2: any, event: any, target?: any): Promise<void> { throw 'mixin?!'; }
    getScriptResult(): Promise<any> { throw 'mixin?!'; }
    createExecutableScriptFunction(script: any): Function { throw 'mixin?!'; }
    hashScriptCode(script: any): Promise<string> { throw 'mixin?!'; }
    markScriptTrusted(): Promise<void> { throw 'mixin?!'; }
*/
}


